<script setup>
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { api } from '@/lib/http.js'
import { enumLabel } from '@/lib/labels.js'
import { useUi } from '@/stores/ui.js'
import { reportError } from '@/composables/useSave.js'
import UiModal from '@/components/ui/UiModal.vue'
import UiField from '@/components/ui/UiField.vue'
import UiButton from '@/components/ui/UiButton.vue'
import RefPicker from '@/components/form/RefPicker.vue'
import OwnerPicker from '@/components/form/OwnerPicker.vue'
import DateTimeInput from '@/components/form/DateTimeInput.vue'
import RichTextEditor from '@/components/form/RichTextEditor.vue'

/**
 * A quick campaign (Dynamics, D-139): one activity for every member of a marketing list — a
 * call, task, meeting or an e-mail to write — on whoever works each member, on me, or on a
 * chosen person; confirmed with the number it will make.
 */
const props = defineProps({ open: Boolean, list: { type: Object, required: true }, count: { type: Number, default: 0 } })
const emit = defineEmits(['close', 'done'])
const { t } = useI18n()
const ui = useUi()
const form = ref({})
const busy = ref(false)
const tomorrow = () => { const d = new Date(Date.now() + 86400000); d.setHours(9, 0, 0, 0); return d.toISOString() }
watch(() => props.open, (o) => {
  if (o) form.value = { type: 'call', subject: '', description: null, due_at: tomorrow(), owner: 'member_owner', ownerPick: null, campaign_uuid: null }
}, { immediate: true })

async function run() {
  if (!(await ui.ask({ title: t('marketing.quickCampaign'), message: t('marketing.quickConfirm', { n: props.count, type: enumLabel('activity', 'type', form.value.type) }), confirmLabel: t('marketing.quickRun') }))) return
  busy.value = true
  try {
    const f = form.value
    const res = await api(`/marketing-lists/${props.list.uuid}/quick-campaign`, { method: 'POST', timeoutMs: 120000, body: {
      type: f.type, subject: f.subject, description: f.description, due_at: f.due_at, owner: f.owner, owner_uuid: f.owner === 'user' ? f.ownerPick?.uuid : null, campaign_uuid: f.campaign_uuid || null,
    } })
    ui.success(t('marketing.quickMade', { n: res.data.created }))
    emit('done')
  } catch (e) {
    reportError(e)
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <UiModal :open="open" :title="$t('marketing.quickCampaign')" size="lg" @close="emit('close')">
    <div class="grid gap-3 sm:grid-cols-2" data-testid="quick-campaign-form">
      <UiField :label="$t('fields.activity.type')">
        <select v-model="form.type" class="input"><option v-for="x in ['call', 'task', 'meeting', 'email']" :key="x" :value="x">{{ enumLabel('activity', 'type', x) }}</option></select>
      </UiField>
      <UiField :label="form.type === 'meeting' ? $t('fields.activity.start_at') : $t('fields.activity.due_at')"><DateTimeInput v-model="form.due_at" /></UiField>
      <UiField :label="$t('fields.activity.subject')" required class="sm:col-span-2"><input v-model="form.subject" class="input" data-testid="quick-subject" /></UiField>
      <UiField :label="$t('fields.common.description')" class="sm:col-span-2"><RichTextEditor v-model="form.description" compact palette min-height="4rem" /></UiField>
      <UiField :label="$t('marketing.quickOwner')">
        <select v-model="form.owner" class="input">
          <option value="member_owner">{{ $t('marketing.ownerMember') }}</option>
          <option value="me">{{ $t('marketing.ownerMe') }}</option>
          <option value="user">{{ $t('marketing.ownerUser') }}</option>
        </select>
      </UiField>
      <UiField v-if="form.owner === 'user'" :label="$t('entities.user.one')"><OwnerPicker v-model="form.ownerPick" users-only /></UiField>
      <UiField :label="$t('entities.campaign.one')" :class="form.owner === 'user' ? 'sm:col-span-2' : ''"><RefPicker v-model="form.campaign_uuid" entity="campaign" /></UiField>
    </div>
    <p class="mt-3 text-sm text-muted"><i class="fa-solid fa-circle-info" aria-hidden="true"></i> {{ $t('marketing.quickHint', { n: count }) }}</p>
    <template #footer>
      <UiButton @click="emit('close')">{{ $t('common.cancel') }}</UiButton>
      <UiButton variant="primary" icon="fa-bolt" :loading="busy" :disabled="!form.subject?.trim() || (form.owner === 'user' && !form.ownerPick)" data-testid="quick-run" @click="run">{{ $t('marketing.quickRun') }}</UiButton>
    </template>
  </UiModal>
</template>
